import type { AppDispatch } from "../../../store.ts"
import { DEFAULT_LAYERS } from "../redux/global-map.constants.ts"
import {
  clearFilters,
  clearListSelection,
  setActiveLayers,
  setListEntity
} from "../redux/global-map.slice.ts"
import type { MapLayer } from "../redux/global-map.types.ts"

/**
 * The one "Clear All" dispatch sequence for the Global Map — built for
 * `GlobalMapFilterBar`'s own Clear All button (Task 6): `clearFilters()`,
 * `setActiveLayers(DEFAULT_LAYERS)` only when the "Projects & Leads" layer
 * toggle is actually narrowed (set-equality, not array-order equality — see
 * `isLayersNarrowed` below), and — List-tab only — `setListEntity("all")` +
 * `clearListSelection()`, since a filters reset is a genuinely fresh List
 * query the same way a sub-tab change is.
 *
 * `ListTab.tsx`'s own empty-state "Clear Filters" button (Task 14) calls
 * this SAME function rather than re-listing the same dispatches a second
 * time — extracted specifically so the two can never silently diverge if
 * one is edited without the other (flagged in Task 14 review: they were
 * independently re-implemented and happened to agree, which isn't the same
 * guarantee as sharing one implementation).
 *
 * Search-term clearing is deliberately NOT part of this function. On the Map
 * tab, clearing search goes through `useMapFeatures`'s own `clearSearch()`
 * (`GlobalMapFilterBar`'s `onClearSearch` prop), which also resets the map's
 * camera/viewport state — genuinely more work than a bare
 * `dispatch(setSearchTerm(""))`, and not something this plain Redux helper
 * can or should reach into. `ListTab.tsx` has no such camera state, so it
 * clears search itself with a plain `dispatch(setSearchTerm(""))` right
 * after calling this function.
 */
export function clearAllFilters(
  dispatch: AppDispatch,
  activeLayers: MapLayer[],
  { includeListReset }: { includeListReset: boolean }
) {
  const isLayersNarrowed =
    activeLayers.length !== DEFAULT_LAYERS.length ||
    !DEFAULT_LAYERS.every((layer) => activeLayers.includes(layer))

  dispatch(clearFilters())
  if (isLayersNarrowed) dispatch(setActiveLayers(DEFAULT_LAYERS))

  if (includeListReset) {
    dispatch(setListEntity("all"))
    dispatch(clearListSelection())
  }
}
